import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { X } from 'lucide-react';
import { FairPriceData, ForecastData, ForecastCommentary, ChartData, PolymerType, POLYMER_LABELS, dataService } from '@rti/shared';
import { useTheme } from '@/contexts/ThemeContext';
import PolymerSelector from '../components/PolymerSelector';
import ForecastChart from '../components/ForecastChart';
import PriceHistoryChart from '../components/PriceHistoryChart';
import FairPriceExportButton from '../components/FairPriceExportButton';
import CommentaryPanel from '../components/CommentaryPanel';
import { formatCommentary } from '@/utils/formatCommentary';
import { trackUserEvent } from "@/utils/userTracking";

const PricingForecast: React.FC = () => {
  const { type } = useParams<{ type?: string }>();
  const navigate = useNavigate();
  const { isDark } = useTheme();

  const [loading, setLoading] = useState(true);
  const [selectedPolymer, setSelectedPolymer] = useState<PolymerType>(
    type === 'pp' ? 'PPHomo' :
    type === 'ps' ? 'GPPS' :
    type === 'pvc' ? 'PVC' :
    type === 'pet' ? 'PETFormula' :
    'HDPEBlowMLDG'
  );
  const [forecastCommentary, setForecastCommentary] = useState<ForecastCommentary[]>([]);
  const [allExpanded, setAllExpanded] = useState(false);
  const [chartData, setChartData] = useState<ChartData | null>(null);
  const [polymerCategory, setPolymerCategory] = useState<'PE' | 'PP' | 'PS' | 'PVC' | 'PET'>(
    type === 'pp' ? 'PP' :
    type === 'ps' ? 'PS' :
    type === 'pvc' ? 'PVC' :
    type === 'pet' ? 'PET' :
    'PE'
  );
  const [commentaryPolymer, setCommentaryPolymer] = useState(
    type === 'pp' ? 'PP' :
    type === 'ps' ? 'PS' :
    type === 'pvc' ? 'PVC' :
    type === 'pet' ? 'PET' :
    'PE'
  );
  const [chartLoading, setChartLoading] = useState(true);
  const [showMethodology, setShowMethodology] = useState(false);
  
  const [rawFairPriceData, setRawFairPriceData] = useState<FairPriceData[]>([]);
  const [rawForecastData, setRawForecastData] = useState<ForecastData[]>([]);
  const fetchInProgressRef = useRef(false);

  
  useEffect(() => {
    if (type === 'pp') {
      setSelectedPolymer('PPHomo');
      setPolymerCategory('PP');
      setCommentaryPolymer('PP');
    } else if (type === 'ps') {
      setSelectedPolymer('GPPS');
      setPolymerCategory('PS');
      setCommentaryPolymer('PS');
    } else if (type === 'pvc') {
      setSelectedPolymer('PVC');
      setPolymerCategory('PVC');
      setCommentaryPolymer('PVC');
    } else if (type === 'pet') {
      setSelectedPolymer('PETFormula');
      setPolymerCategory('PET');
      setCommentaryPolymer('PET');
    } else {
      setSelectedPolymer('HDPEBlowMLDG');
      setPolymerCategory('PE');
      setCommentaryPolymer('PE');
    }
  }, [type]);

  const fetchChartData = async () => {
    
    if (fetchInProgressRef.current) return;

    fetchInProgressRef.current = true;

    try {
      setLoading(true);

      
      const [fairPriceData, forecastData, commentaryData] = await Promise.all([
        dataService.getFairPriceData(),
        dataService.getForecastData(),
        dataService.getForecastCommentary()
      ]);

      
      setRawFairPriceData(fairPriceData || []);
      setRawForecastData(forecastData || []);

      processChartData(fairPriceData || [], forecastData || []);
      processCommentary(commentaryData.tblForecastCommentary || commentaryData, commentaryPolymer);

    } catch (error) {
      console.error('Data fetching error:', error);
    } finally {
      setLoading(false);
      fetchInProgressRef.current = false;
    }
  };

  const getPolymerValue = (d: FairPriceData, key: PolymerType): number | null => {
    const value = (d as unknown as Record<string, unknown>)[key];
    const num = typeof value === 'number' ? value : Number(value);
    return isNaN(num) ? null : num;
  };

  const processChartData = (fairPrice: FairPriceData[], forecast: ForecastData[]) => {
    setChartLoading(true);
    
    fairPrice.sort((a, b) => new Date(a.Date).getTime() - new Date(b.Date).getTime());

    
    const today = new Date();
    const historicalStartDate = new Date(today);
    historicalStartDate.setMonth(today.getMonth() - 18);
    const futureEndDate = new Date(today);
    futureEndDate.setMonth(today.getMonth() + 12);

    
    const priceHistoryData = fairPrice.filter(d => {
      const date = new Date(d.Date);
      return date >= historicalStartDate && date <= futureEndDate;
    }).map(d => ({ x: d.Date, y: getPolymerValue(d, selectedPolymer) }));

    const fairPriceData = priceHistoryData;

    const lastFairPoint = fairPriceData.at(-1);
    if (!lastFairPoint) {
      setChartLoading(false);
      return;
    }

    const latestFairPriceDate = new Date(lastFairPoint.x);

    const filteredForecast = forecast.filter(d => {
      const fDate = new Date(d.Date_Quarterly);
      return fDate > latestFairPriceDate;
    }).sort((a, b) => {
      
      return new Date(a.Date_Quarterly).getTime() - new Date(b.Date_Quarterly).getTime();
    });

    const highKey = 
      polymerCategory === 'PE' ? 'PE_High' :
      polymerCategory === 'PP' ? 'PP_Homo_High' :
      polymerCategory === 'PS' ? 'PS_High' :
      polymerCategory === 'PVC' ? 'PVC_High' :
      polymerCategory === 'PET' ? 'PET_High' :
      'PE_High';
    
    const lowKey = 
      polymerCategory === 'PE' ? 'PE_Low' :
      polymerCategory === 'PP' ? 'PP_Homo_Low' :
      polymerCategory === 'PS' ? 'PS_Low' :
      polymerCategory === 'PVC' ? 'PVC_Low' :
      polymerCategory === 'PET' ? 'PET_Low' :
      'PE_Low';

    
    let runningHigh = parseFloat(String(lastFairPoint.y)) || 0;
    let runningLow = parseFloat(String(lastFairPoint.y)) || 0;

    const midForecast = filteredForecast.map(d => {
      const high = parseFloat(String(d[highKey as keyof ForecastData])) || 0;
      const low = parseFloat(String(d[lowKey as keyof ForecastData])) || 0;
      runningHigh += high;
      runningLow += low;

      return { x: d.Date_Quarterly, y: +((runningHigh + runningLow) / 2).toFixed(3) };
    });

    
    runningHigh = parseFloat(String(lastFairPoint.y)) || 0;
    const upperForecast = filteredForecast.map(d => {
      const high = parseFloat(String(d[highKey as keyof ForecastData])) || 0;
      runningHigh += high;
      return { x: d.Date_Quarterly, y: +runningHigh.toFixed(3) };
    });

    
    runningLow = parseFloat(String(lastFairPoint.y)) || 0;
    const lowerForecast = filteredForecast.map(d => {
      const low = parseFloat(String(d[lowKey as keyof ForecastData])) || 0;
      runningLow += low;
      return { x: d.Date_Quarterly, y: +runningLow.toFixed(3) };
    });

    setChartData({
      datasets: [], labels: [],
      fairPrice: fairPriceData,
      mid: [lastFairPoint, ...midForecast],
      upper: [lastFairPoint, ...upperForecast],
      lower: [lastFairPoint, ...lowerForecast],
      history: priceHistoryData
    });

    
    setTimeout(() => {
      setChartLoading(false);
    }, 300);
  };

  const processCommentary = (allCommentary: ForecastCommentary[], polymerKey: string) => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth() + 1;
    const currentQuarter = Math.ceil(currentMonth / 3);

    const filtered = allCommentary.filter(c => c.polymer === polymerKey);
    const sorted = filtered
      .filter(d => d.active)
      .filter(d => {
        if (d.forecastYear > currentYear) return true;
        if (d.forecastYear === currentYear && d.forecastQtr >= currentQuarter) return true;
        return false;
      })
      .sort((a, b) => {
        const aDate = new Date(`${a.forecastYear}-${String((a.forecastQtr - 1) * 3 + 1).padStart(2, '0')}-01`);
        const bDate = new Date(`${b.forecastYear}-${String((b.forecastQtr - 1) * 3 + 1).padStart(2, '0')}-01`);
        return aDate.getTime() - bDate.getTime();
      });

    sorted.forEach((d, i) => {
      d.expanded = i === 0;
      d.commentary = formatCommentary(d.commentary);
    });

    setForecastCommentary(sorted);
  };

  const handlePolymerChange = (polymerKey: PolymerType) => {
    setSelectedPolymer(polymerKey);
    trackUserEvent('forecast_polymer_changed', `Forecast Polymer Changed to ${POLYMER_LABELS[polymerKey]}`);
  };

  const toggleCommentary = (item: ForecastCommentary) => {
    setForecastCommentary(prev =>
      prev.map(c => c === item ? { ...c, expanded: !c.expanded } : c)
    );
  };

  const toggleAllCommentary = () => {
    const shouldExpand = !allExpanded;
    setForecastCommentary(prev =>
      prev.map(c => ({ ...c, expanded: shouldExpand }))
    );
    setAllExpanded(shouldExpand);
  };

  const handleBackToDashboard = () => {
    navigate('/dashboard');
  };

  
  useEffect(() => {
    fetchChartData();
  }, [commentaryPolymer]);

  
  useEffect(() => {
    if (rawFairPriceData.length > 0 && rawForecastData.length > 0) {
      processChartData(rawFairPriceData, rawForecastData);
    }
  }, [selectedPolymer, polymerCategory]);

  if (loading) {
    return (
      <div data-testid="app-shell" className="text-3xl flex text-rs-off-white items-center justify-center h-[500px] w-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
      </div>
    );
  }

  return (
    <div data-testid="app-shell" className="h-full flex flex-col p-5 w-full dashboard-shadow max-w-8xl mt-5">
      <div className="content-toolbar">
        <button
          className="jet flex items-center gap-2 px-4 py-2 bg-rs-jet text-white rounded"
          onClick={handleBackToDashboard}
        >
          <span className="material-symbols-outlined">arrow_back_ios</span>
          Back to Landing Page
        </button>
      </div>

      <div className="flex justify-between items-center pt-5 pb-3">
        <p className="text-2xl font-light" style={{ color: isDark ? '#f3f4f6' : '#1e2019' }}>
          {POLYMER_LABELS[selectedPolymer]} Fair Price & Forecast Analysis
        </p>
      </div>

      <hr className="my-3 pb-5 -mx-5 border-rs-jet" />

      
      <div data-testid="polymer-selector" className='flex justify-between flex-col md:flex-row gap-4 md:gap-0'>
        <PolymerSelector
          polymers={POLYMER_LABELS}
          selectedPolymer={selectedPolymer}
          onPolymerChange={handlePolymerChange}
          polymerCategory={polymerCategory}
        />
        <div className='mb-7 flex justify-center md:block'>
         <button
          onClick={() => setShowMethodology(true)}
          className="px-4 py-2 bg-rs-green !text-black text-sm rounded-md font-medium hover:bg-rs-green/80 transition-all"
        >
          Forecast Methodology
        </button>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 grid-rows-2 gap-5">
        <div data-testid="forecast-chart" className="bg-rs-off-black rounded-lg p-5 flex items-center justify-center flex-col">
          <h4 className="text-xl text-center mb-3 font-medium" style={{ color: isDark ? '#f3f4f6' : '#1e2019' }}>
            {POLYMER_LABELS[selectedPolymer]} Forecast
          </h4>
          <div data-tour="fair-price-chart" className="h-half w-full min-h-[380px]">
            {chartLoading ? (
              <div className="flex items-center justify-center h-full">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-rs-dark-green"></div>
              </div>
            ) : (
              <ForecastChart chartData={chartData} />
            )}
          </div>
        </div>

        <CommentaryPanel
          forecastCommentary={forecastCommentary}
          allExpanded={allExpanded}
          onToggleCommentary={toggleCommentary}
          onToggleAllCommentary={toggleAllCommentary}
        />

        <div data-testid="price-history-chart" className="bg-rs-off-black rounded-lg p-5 flex items-center justify-center flex-col">
          <div className="w-full flex items-center justify-between mb-3">
            <h4 className="text-xl font-medium text-rs-off-white">
              {POLYMER_LABELS[selectedPolymer]} Price History
            </h4>
            <FairPriceExportButton
              polymer={polymerCategory}
              rawFairPriceData={rawFairPriceData}
              disabled={chartLoading}
            />
          </div>
          <div className="h-half w-full min-h-[380px]">
            {chartLoading ? (
              <div className="flex items-center justify-center h-full">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-rs-dark-green"></div>
              </div>
            ) : (
              <PriceHistoryChart chartData={chartData} />
            )}
          </div>
        </div>
      </div>

      {showMethodology && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-rs-off-black rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-auto border border-rs-green border-opacity-30">
            <div className="sticky top-0 bg-rs-off-black flex justify-between items-center p-6 border-b border-rs-green border-opacity-30">
              <h2 className="text-2xl font-semibold" style={{ color: isDark ? '#f3f4f6' : '#1e2019' }}>Forecast Methodology</h2>
              <button
                onClick={() => setShowMethodology(false)}
                className="text-rs-light-gray hover:text-rs-off-white transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6">
              <section className="mb-6">
                 <h3 className="bg-rs-dark-green text-rs-off-black px-4 py-2 rounded mb-3 text-lg">Overview</h3>
                <div className="leading-relaxed" style={{ color: isDark ? '#f3f4f6' : '#1e2019' }}>
                  <p className="mb-2">ResinSmart publishes a quarterly forecasted fair market price outlook for various resin products for the North American market owned by ResinSmart AI. The goal is to provide resin buyers with directional pricing based upon forecasted market conditions.</p>
                  <p className="mb-2">The ResinSmart price forecasts are based on a combination of quantitative fundamental and qualitative factors by our market consultants. No specific weighting is given to these elements, and these are based upon current baseline market conditions and forecasted supply and demand events.</p>
                  <p>The intent is to provide resin buyers with directional pricing expectations based on forecasted market conditions—not to publish assessed prices or establish a pricing index.</p>
                </div>
              </section>

              <section className="mb-6">
                 <h3 className="bg-rs-dark-green text-rs-off-black px-4 py-2 rounded mb-3 text-lg">Approval Process</h3>
                <div className="leading-relaxed" style={{ color: isDark ? '#f3f4f6' : '#1e2019' }}>
                  <p>The ResinSmart fair market price forecasts undergo a consultant review process. Under this process, the resin consultant will submit an outline of their forecast and assumptions for internal ResinSmart review. Consultant users will then provide input and recommend any changes before final approval.</p>
                </div>
              </section>

              <section className="mb-6">
                <h3 className="bg-rs-dark-green text-rs-off-black px-4 py-2 rounded mb-3 text-lg">Publication and Timing</h3>
                <div className="leading-relaxed" style={{ color: isDark ? '#f3f4f6' : '#1e2019' }}>
                  <p className="mb-2">ResinSmart updates quarterly forecasts on the ResinSmart AI platform in a digital format. The forecasts will be published within the first 15 days after the start of a new quarter.</p>
                  <p>Interim updates may be issued on an "as needed" basis.</p>
                </div>
              </section>

              <section className="mb-6">
                <h3 className="bg-rs-dark-green text-rs-off-black px-4 py-2 rounded mb-3 text-lg">Change Process</h3>
                <div className="leading-relaxed" style={{ color: isDark ? '#f3f4f6' : '#1e2019' }}>
                  <p>The ResinSmart team regularly reviews its modeling approach mid-quarter and determines if additional data sets should be added to continuously improve the forecast accuracy. At its discretion, the ResinSmart team may undertake a formal consultation and a new run on changes to existing data sets. ResinSmart commits to communicating any material changes for its modeling approach and notes this in the methodology reference guide.</p>
                </div>
              </section>

              <section className="mb-6">
                 <h3 className="bg-rs-dark-green text-rs-off-black px-4 py-2 rounded mb-3 text-lg">Forecast Prices</h3>
                <div className="leading-relaxed" style={{ color: isDark ? '#f3f4f6' : '#1e2019' }}>
                  <p className="mb-2">ResinSmart forecasts fair market resin prices for North America based upon market conditions and market transactional data points.</p>
                  <p className="mb-2">Forecasts are based on various commodity resin grades, provide in industry average price comparison between regions.</p>
                  <p>Forecasted resins are noted with low, mid, and high range forecasts.</p>
                </div>
              </section>

              <section className="mb-6">
                <h3 className="bg-rs-dark-green text-rs-off-black px-4 py-2 rounded mb-3 text-lg">Revisions to Outlook</h3>
                <div className="leading-relaxed" style={{ color: isDark ? '#f3f4f6' : '#1e2019' }}>
                  <p className="mb-2">The ResinSmart team will conduct a quarterly rolling review for adjustments for its price forecasts and modelled number based upon new information or a change of opinion. These reviews are scheduled on a regular cadence. Upon these reviews, if any corrections are needed, we correct them more accordingly.</p>
                  <p>At any time, ResinSmart may revise its outlook in the event of a significant or unexpected change in market conditions, including—but not limited to—major outages, geopolitical disruptions, logistics constraints, or feedstock volatility.</p>
                </div>
              </section>

              <section className="mb-6">
                <h3 className="bg-rs-dark-green text-rs-off-black px-4 py-2 rounded mb-3 text-lg">Forecast Process</h3>
                <div className="leading-relaxed" style={{ color: isDark ? '#f3f4f6' : '#1e2019' }}>
                  <p className="mb-2">ResinSmart's consultants will determine and analyze any factors they consider relevant to providing a credible forecast. The forecast will contain data sets for quantitative modeling. Data includes (but is not limited to) the following: supply and demand outlook, capacity changes, scheduled maintenance activities, trade flows and industry trends. ResinSmart also includes qualitative industry factors as well as data touch points from client interactions. These client data points are kept in strict confidence and allow for short-term transactional data trend analysis.</p>
                  <p>The forecast process begins with feedstock cash costs models with consideration given to different regional conversion costs. North American economic forecasts factor into demand patterns as well as global export forecasted demand.</p>
                </div>
              </section>

              <section>
                 <h3 className="bg-rs-dark-green text-rs-off-black px-4 py-2 rounded mb-3 text-lg">Ethics and Compliance</h3>
                <div className="leading-relaxed" style={{ color: isDark ? '#f3f4f6' : '#1e2019' }}>
                  <p className="mb-2">The ResinSmart team operates according to the best unbiased practices and maintain position consistent with ethical standards. The policy can be found on our website at <a href="https://www.ResinSmart.ai" className="text-rs-green hover:underline">www.ResinSmart.ai</a>.</p>
                  <p>ResinSmart's forecasts are derived exclusively from our internal research, which data sets and consultant analysis, and are not influenced by external pressures, commercial relationships, or any factors outside of our independent market evaluation.</p>
                </div>
              </section>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PricingForecast;
